import { useEffect, useState, type FormEvent } from 'react';
import { applyTheme } from '../theme';
import { adminApi, errorText, type ApiKeyRow, type TenantSettings } from './api';
import { useAdmin } from './app';
import { Card, Field, ImageUpload, SaveBar, Tag, confirmAction, formatDateTime, useSaver } from './ui';

/** Erscheinungsbild (Konzept 9): ein Aufbau, je Anbieter Grundton, Farben, Schrift, Logo. */
export function DesignPage() {
  const { identity, setTenant } = useAdmin();
  const t = identity.tenant;
  const theme = t.theme as { scheme?: string; brand?: string; brand2?: string; action?: string; font?: string; logoUrl?: string; logoDarkUrl?: string; iconUrl?: string };
  const toForm = (x: typeof theme) => ({ scheme: x.scheme === 'light' ? 'light' : 'dark', brand: x.brand ?? '', brand2: x.brand2 ?? '', action: x.action ?? '', font: x.font ?? 'sans' });
  const [form, setForm] = useState(toForm(theme));
  const saver = useSaver();
  useEffect(() => setForm(toForm(theme)), [t.theme]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(theme));

  // Vorschau sofort – gespeichert wird erst mit dem Knopf.
  useEffect(() => {
    applyTheme({ scheme: form.scheme as 'light' | 'dark', ...(form.brand ? { brand: form.brand } : {}), ...(form.brand2 ? { brand2: form.brand2 } : {}), ...(form.action ? { action: form.action } : {}), font: form.font as 'sans' | 'serif' | 'rounded' });
  }, [form]);

  function save(e: FormEvent) {
    e.preventDefault();
    void saver.run(async () => setTenant(await adminApi.updateTenant({ theme: { scheme: form.scheme, brand: form.brand || null, brand2: form.brand2 || null, action: form.action || null, font: form.font } })));
  }

  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Erscheinungsbild</h1>
          <p className="muted">Ein Aufbau für alle Akademien – deine Farben, dein Logo, deine Schrift. Änderungen siehst du hier sofort, gespeichert wird mit dem Knopf.</p>
        </div>
      </div>
      <Card as="form" onSubmit={save} title="Farben und Schrift">
        <div className="grid2">
          <Field label="Grundton" id="d-scheme" hint="Dunkel wirkt wie eine Bühne, hell wie ein Arbeitsplatz.">
            <select id="d-scheme" value={form.scheme} onChange={(e) => setForm({ ...form, scheme: e.target.value })}>
              <option value="dark">Dunkel</option>
              <option value="light">Hell</option>
            </select>
          </Field>
          <Field label="Schrift" id="d-font">
            <select id="d-font" value={form.font} onChange={(e) => setForm({ ...form, font: e.target.value })}>
              <option value="sans">Klar (serifenlos)</option>
              <option value="serif">Klassisch (mit Serifen)</option>
              <option value="rounded">Freundlich (rund)</option>
            </select>
          </Field>
          <ColorField label="Akzentfarbe" id="d-brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} hint="Überschriften-Zeile zwei, Nummern, kleine Zeilen." />
          <ColorField label="Zweite Farbe (Verlauf)" id="d-brand2" value={form.brand2} onChange={(v) => setForm({ ...form, brand2: v })} hint="Fortschrittsbalken laufen von Akzent zu dieser Farbe. Leer = einfarbig." />
          <ColorField label="Knopffarbe" id="d-action" value={form.action} onChange={(v) => setForm({ ...form, action: v })} hint="Leer = Akzentfarbe." />
        </div>
        <SaveBar saver={saver} dirty={dirty} />
      </Card>
      <Card title="Logo">
        <div className="grid2">
          <Field label="Für hellen Hintergrund" hint="PNG mit transparentem Hintergrund, mindestens 600 Pixel breit.">
            <ImageUpload current={theme.logoUrl ?? null} alt="Logo hell" previewClass="image-upload__preview--light" hint="" onUpload={async (f) => setTenant(await adminApi.setLogo('light', f))} onRemove={async () => setTenant(await adminApi.removeLogo('light'))} />
          </Field>
          <Field label="Für dunklen Hintergrund" hint="Dieselbe Marke, aber in Weiß oder hellen Farben. Leer = Logo für hellen Hintergrund.">
            <ImageUpload current={theme.logoDarkUrl ?? null} alt="Logo dunkel" previewClass="image-upload__preview--dark" hint="" onUpload={async (f) => setTenant(await adminApi.setLogo('dark', f))} onRemove={async () => setTenant(await adminApi.removeLogo('dark'))} />
          </Field>
        </div>
      </Card>
      <Card title="App-Icon">
        <p className="muted small">Erscheint auf dem Home-Bildschirm, wenn Kunden deine Akademie als App ablegen. Quadratisch, mindestens 512 × 512, ohne Text – ein Bildzeichen wirkt am besten. Ohne eigenes Icon zeigt die Plattform den Anfangsbuchstaben deiner Akademie in deinen Farben.</p>
        <div className="icon-preview">
          <img src={`/api/icon.png?v=${encodeURIComponent(theme.iconUrl ?? form.brand)}`} alt="App-Icon" className="icon-preview__img" />
          <ImageUpload current={theme.iconUrl ?? null} alt="App-Icon" previewClass="image-upload__preview--icon" hint="" onUpload={async (f) => setTenant(await adminApi.setLogo('icon', f))} onRemove={async () => setTenant(await adminApi.removeLogo('icon'))} />
        </div>
      </Card>
    </>
  );
}

function ColorField({ label, id, value, onChange, hint }: { label: string; id: string; value: string; onChange: (v: string) => void; hint: string }) {
  const valid = /^#[0-9a-f]{6}$/i.test(value);
  return (
    <Field label={label} id={id} hint={hint}>
      <div className="color-field">
        <input type="color" value={valid ? value : '#888888'} onChange={(e) => onChange(e.target.value)} aria-label={`${label} wählen`} />
        <input id={id} type="text" value={value} placeholder="#f0b431" onChange={(e) => onChange(e.target.value.trim())} pattern="#[0-9a-fA-F]{6}" />
        {value && <button type="button" className="link-button small" onClick={() => onChange('')}>leeren</button>}
      </div>
    </Field>
  );
}

/** Rechtstexte je Anbieter (Konzept 5.7, 7.4). */
export function LegalPage() {
  const { identity, setTenant } = useAdmin();
  const legal = identity.tenant.legal;
  const toForm = (l: Record<string, string>) => ({ impressum: l['impressum'] ?? '', datenschutz: l['datenschutz'] ?? '', agb: l['agb'] ?? '', widerruf: l['widerruf'] ?? '' });
  const [form, setForm] = useState(toForm(legal));
  const saver = useSaver();
  useEffect(() => setForm(toForm(legal)), [legal]);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(legal));
  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Rechtstexte</h1>
          <p className="muted">Du bist Verantwortlicher für deine Kunden – Impressum und Datenschutzerklärung müssen deine sein. Absätze durch Leerzeilen trennen.</p>
        </div>
      </div>
      <Card
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          void saver.run(async () => setTenant(await adminApi.updateTenant({ legal: form })));
        }}
      >
        <Field label="Impressum" id="r-impressum" hint="Erscheint unter /impressum und im Fuß jeder Seite.">
          <textarea id="r-impressum" rows={8} value={form.impressum} onChange={(e) => setForm({ ...form, impressum: e.target.value })} />
        </Field>
        <Field label="Datenschutzerklärung" id="r-datenschutz" hint="Nenne die tatsächlichen Dienstleister: IONOS (Server, Deutschland), Stripe (Zahlung), Brevo (Mail, EU).">
          <textarea id="r-datenschutz" rows={12} value={form.datenschutz} onChange={(e) => setForm({ ...form, datenschutz: e.target.value })} />
        </Field>
        <Field label="AGB" id="r-agb" hint="Optional. Wird beim Kauf verlinkt.">
          <textarea id="r-agb" rows={8} value={form.agb} onChange={(e) => setForm({ ...form, agb: e.target.value })} />
        </Field>
        <Field label="Widerrufsbelehrung" id="r-widerruf" hint="Wird beim Kauf gezeigt. Bei digitalen Inhalten mit sofortigem Zugang erlischt das Widerrufsrecht nur mit ausdrücklicher Zustimmung – das Häkchen dafür setzt die Plattform.">
          <textarea id="r-widerruf" rows={8} value={form.widerruf} onChange={(e) => setForm({ ...form, widerruf: e.target.value })} />
        </Field>
        <SaveBar saver={saver} dirty={dirty} />
      </Card>
    </>
  );
}

/** Einstellungen des Anbieters: Name, Absender, Gerätegrenze, Widerruf, Passwort. */
export function SettingsPage() {
  const { identity, setTenant } = useAdmin();
  const t = identity.tenant;
  const toForm = (x: TenantSettings) => ({ name: x.name, senderEmail: x.senderEmail ?? '', senderName: x.senderName ?? '', deviceLimit: String(x.deviceLimit), withdrawalMode: x.withdrawalMode });
  const [form, setForm] = useState(toForm(t));
  const saver = useSaver();
  useEffect(() => setForm(toForm(t)), [t]);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(t));
  const usedHours = (t.usedSeconds / 3600).toFixed(1).replace('.', ',');
  const quotaHours = t.storageQuotaSeconds ? (t.storageQuotaSeconds / 3600).toFixed(0) : null;

  return (
    <>
      <div className="adm-head">
        <div>
          <h1>Einstellungen</h1>
          <p className="muted">
            {t.customDomain ? <>Deine Akademie läuft unter <strong>{t.customDomain}</strong>.</> : <>Deine Akademie läuft unter <strong>{t.slug}.…</strong></>} Videospeicher: {usedHours} Stunden{quotaHours ? ` von ${quotaHours}` : ''}.
          </p>
        </div>
      </div>
      <Card
        as="form"
        title="Akademie"
        onSubmit={(e) => {
          e.preventDefault();
          void saver.run(async () => setTenant(await adminApi.updateTenant({ name: form.name, senderEmail: form.senderEmail || null, senderName: form.senderName || null, deviceLimit: Number(form.deviceLimit), withdrawalMode: form.withdrawalMode })));
        }}
      >
        <div className="grid2">
          <Field label="Name der Akademie" id="s-name">
            <input id="s-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={200} />
          </Field>
          <Field label="Geräte gleichzeitig" id="s-devices" hint="So viele Geräte dürfen zur selben Zeit abspielen. Zwei reicht für Mac plus Handy.">
            <select id="s-devices" value={form.deviceLimit} onChange={(e) => setForm({ ...form, deviceLimit: e.target.value })}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={String(n)}>{n}</option>
              ))}
            </select>
          </Field>
          <Field label="Absender-Adresse für Mails" id="s-sender" hint="Muss bei Brevo als Absenderdomain bestätigt sein. Leer = Absender der Plattform.">
            <input id="s-sender" type="email" value={form.senderEmail} onChange={(e) => setForm({ ...form, senderEmail: e.target.value })} />
          </Field>
          <Field label="Absender-Name" id="s-sendername">
            <input id="s-sendername" type="text" value={form.senderName} onChange={(e) => setForm({ ...form, senderName: e.target.value })} maxLength={200} />
          </Field>
          <Field label="Widerruf beim Kauf" id="s-withdrawal" hint="Pflicht-Häkchen: Zugang sofort, Widerrufsrecht erlischt (§ 356 Abs. 5 BGB). Garantie: 14 Tage Widerruf bleibt, als Verkaufsargument.">
            <select id="s-withdrawal" value={form.withdrawalMode} onChange={(e) => setForm({ ...form, withdrawalMode: e.target.value as 'waiver' | 'guarantee' })}>
              <option value="waiver">Pflicht-Häkchen, Zugang sofort</option>
              <option value="guarantee">Geld-zurück-Garantie, Widerruf gewähren</option>
            </select>
          </Field>
        </div>
        <SaveBar saver={saver} dirty={dirty} />
      </Card>
      <Card title="Zahlungen">
        <p className="muted small">{t.stripeConnected ? 'Stripe ist verbunden.' : 'Stripe ist noch nicht verbunden. Die Anbindung kommt mit dem nächsten Schritt – bis dahin schaltest du Kunden von Hand frei.'}</p>
      </Card>
      <ApiKeysCard />
      <PasswordCard />
    </>
  );
}

/** Schnittstelle für KI-Assistenten: Schlüssel mit Rechte-Stufen, einmal angezeigt, jederzeit widerrufbar. */
function ApiKeysCard() {
  const [keys, setKeys] = useState<ApiKeyRow[] | null>(null);
  const [scopes, setScopes] = useState<{ scope: string; title: string; description: string }[]>([]);
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>(['read', 'content']);
  const [expires, setExpires] = useState('');
  const [fresh, setFresh] = useState<{ key: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const saver = useSaver();

  const load = () => adminApi.listApiKeys().then((r) => { setKeys(r.keys); setScopes(r.scopes); }).catch(() => setKeys([]));
  useEffect(() => void load(), []);

  const origin = window.location.origin;
  return (
    <Card title="Schnittstelle für KI-Assistenten">
      <p className="muted small">
        Mit einem Schlüssel kann ein KI-Assistent (zum Beispiel Claude) deine Akademie befüllen: Kurse anlegen, Texte schreiben, Sprungmarken setzen, Bilder hochladen. Der Schlüssel kann nur, was die Verwaltung auch kann, und nur bei dieser Akademie. Er wird genau einmal angezeigt. Adresse der Schnittstelle: <code>{origin}/api/admin/…</code>, Schlüssel als <code>Authorization: Bearer …</code>.
      </p>
      {fresh && (
        <div className="hint" style={{ marginTop: 12 }}>
          <strong>Dein neuer Schlüssel „{fresh.name}“ – kopiere ihn jetzt, er wird nicht noch einmal angezeigt:</strong>
          <div className="row" style={{ marginTop: 8 }}>
            <code className="keybox">{fresh.key}</code>
            <button type="button" className="button button--small" onClick={() => void navigator.clipboard.writeText(fresh.key).then(() => setCopied(true))}>{copied ? 'Kopiert' : 'Kopieren'}</button>
            <button type="button" className="link-button" onClick={() => { setFresh(null); setCopied(false); }}>Ausblenden</button>
          </div>
        </div>
      )}
      {keys === null ? <p className="muted">Wird geladen …</p> : keys.length > 0 && (
        <table className="table" style={{ marginTop: 12 }}>
          <thead>
            <tr><th>Name</th><th>Beginnt mit</th><th>Rechte</th><th>Zuletzt benutzt</th><th>Gültig bis</th><th></th></tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className={k.revokedAt ? 'row--muted' : ''}>
                <td><strong>{k.name}</strong></td>
                <td><code>{k.prefix}…</code></td>
                <td>{k.scopes.map((sc) => scopes.find((x) => x.scope === sc)?.title ?? sc).join(', ')}</td>
                <td className="muted">{formatDateTime(k.lastUsedAt)}</td>
                <td className="muted">{k.revokedAt ? <Tag tone="muted">widerrufen</Tag> : k.expiresAt ? formatDateTime(k.expiresAt) : 'unbegrenzt'}</td>
                <td>{!k.revokedAt && <button type="button" className="link-button small link-button--danger" onClick={() => { if (confirmAction(`Schlüssel „${k.name}“ widerrufen? Ein KI-Assistent, der ihn nutzt, verliert sofort den Zugriff.`)) void adminApi.revokeApiKey(k.id).then(load); }}>Widerrufen</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form
        className="grant"
        onSubmit={(e) => {
          e.preventDefault();
          void saver.run(async () => {
            const r = await adminApi.createApiKey(name.trim() || 'Schlüssel', chosen, expires ? Number(expires) : null);
            setFresh({ key: r.key, name: r.row.name });
            setCopied(false);
            setName('');
            await load();
          });
        }}
      >
        <span className="kicker">Neuen Schlüssel erzeugen</span>
        <div className="grid2">
          <Field label="Name" id="key-name" hint="Wofür oder für wen, z. B. „Claude auf Rainers Mac“.">
            <input id="key-name" type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
          </Field>
          <Field label="Gültig für" id="key-expires">
            <select id="key-expires" value={expires} onChange={(e) => setExpires(e.target.value)}>
              <option value="">Unbegrenzt</option>
              <option value="30">30 Tage</option>
              <option value="90">90 Tage</option>
              <option value="365">1 Jahr</option>
            </select>
          </Field>
        </div>
        <Field label="Rechte-Stufen" hint="Für einen KI-Assistenten reichen meist Lesen und Inhalte. Löschen nur, wenn du es wirklich brauchst.">
          <div className="checks">
            {scopes.map((sc) => (
              <label key={sc.scope} className="check">
                <input type="checkbox" checked={sc.scope === 'read' || chosen.includes(sc.scope)} disabled={sc.scope === 'read'} onChange={(e) => setChosen(e.target.checked ? [...chosen, sc.scope] : chosen.filter((x) => x !== sc.scope))} />
                <span><strong>{sc.title}</strong> <span className="muted small">– {sc.description}</span></span>
              </label>
            ))}
          </div>
        </Field>
        <div className="row-actions">
          <button type="submit" className="button" disabled={saver.busy || !name.trim()}>Schlüssel erzeugen</button>
          {saver.error && <span className="error-inline">{errorText(saver.error)}</span>}
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const saver = useSaver();
  return (
    <Card
      as="form"
      title="Passwort ändern"
      onSubmit={(e) => {
        e.preventDefault();
        void saver.run(async () => {
          await adminApi.changePassword(current, next);
          setCurrent('');
          setNext('');
        });
      }}
    >
      <div className="grid2">
        <Field label="Aktuelles Passwort" id="pw-current">
          <input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </Field>
        <Field label="Neues Passwort" id="pw-next" hint="Mindestens 10 Zeichen. Andere Geräte werden abgemeldet.">
          <input id="pw-next" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={10} />
        </Field>
      </div>
      <SaveBar saver={saver} dirty={current.length > 0 && next.length >= 10} label="Passwort ändern" />
    </Card>
  );
}
